const React = require('react');
const Snackbar = require('@andes/snackbar');
const Button = require('@andes/button');
const ArrowDown20 = require('@andes/icons/ArrowDown20');
const APIDetailsContainer = require('./APIDetailsContainer');
const ParamsContainer = require('./ParamsContainer');
const SnippetContainer = require('./SnippetContainer');
const Parser = require('./parser');
const FeedbackThumbs = require('../FeedbackThumbs');
const NotFound = require('../NotFound');
const { useApiRefContextProvider } = require('../../../providers/apiRefContextProvider');
const { getEventTracker } = require('../../../utils/event-tracker');
const { SSRPortal } = require('../../../utils/jsx-utils');
const { Message } = require('@andes/message');

const { useState, useEffect, useRef } = React;

const useElementWasScrolled = (ref) => {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    // Check if the element is intersecting and if the top of the element is not visible
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting || entry.boundingClientRect.top < 0),
    );
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return isIntersecting;
};

const APIReference = (props) => {
  const {
    i18n,
    referenceContent,
    lang,
    deviceType,
    platform,
  } = props;

  const { actualParams, setActualParams, updateActualPageData, setRequestParamsData, getRequestParameters } = useApiRefContextProvider();

  const { paths, url } = referenceContent;
  const serverEndpoint = url;
  const apiEndpoint = Object.keys(paths)[0];
  const Tracker = getEventTracker();
  const responseContainerRef = useRef(null);
  const isResponseContainerVisible = useElementWasScrolled(responseContainerRef);
  const [showAnchorButton, setShowAnchorButton] = useState(false);

  const scrollToResponseContainer = () => {
    if (responseContainerRef.current) {
      responseContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const [apiSnackbar, setApiSnackbar] = useState({
    snackbarData: {
      show: false,
      message: '',
      type: 'green',
      delay: 3000,
    },
  });

  if (!paths[apiEndpoint]) {
    return (
      <NotFound className="reference-error-container" i18n={i18n} />
    );
  }

  const apiMethod = Object.keys(paths[apiEndpoint])[0];
  const apiData = paths[apiEndpoint][apiMethod];
  const codeContentRequestData = Parser.getRequestContent(apiData?.requestBody?.content?.['application/json']?.schema);
  const [newApiData, setNewApiData] = useState(apiData);
  const [codeType, setCodeType] = useState('bash');
  const getResult = () => {
    let i = 200;
    let response = Parser.getNested(newApiData, `responses.${i}.content.application/json.schema`);
    while (response === undefined && i < 300) {
      // eslint-disable-next-line no-plusplus
      i++;
      response = Parser.getNested(newApiData, `responses.${i}.content.application/json.schema`);
    }
    return response;
  };
  const [responseData, setResponseData] = useState(getResult() || {});

  const contentToParse = Parser.getRequestContent(responseData);
  const codeContentResponse = JSON.stringify(
    responseData.type === 'array' ? [contentToParse] : contentToParse,
    null,
    2,
  );

  const { requestParamsData, appCredentials, responseDataContent } = useApiRefContextProvider();

  const liveTestResponse = responseDataContent ? JSON.stringify(responseDataContent?.data, null, 2) : null;

  const headerData = Parser.getHeaderParamContent(requestParamsData, appCredentials?.access_token, apiEndpoint);
  const bodyData = Parser.getRequestContentForLiveTest(requestParamsData);
  const queryStringData = Parser.getQueryParamContent(requestParamsData);

  const initialCodeContentRequest = Parser.getContentRequest(
    apiMethod,
    serverEndpoint,
    apiEndpoint,
    queryStringData,
    JSON.stringify(bodyData, null, 2),
    headerData,
    referenceContent,
    codeType,
  );
  const [codeContentRequest, setCodeContentRequest] = useState(initialCodeContentRequest);
  const [updateCodeContentRequestFromParams, setUpdateCodeContentRequestFromParams] = useState(false);

  const { requestParameters } = useApiRefContextProvider();

  const initResponseParameters = Parser.parseRequestBodyProperties(
    responseData.type === 'array'
      ? responseData.items.properties
      : responseData.properties,
  );
  if (initResponseParameters.length <= 0) {
    initResponseParameters.push({
      description:
      {
        es: 'Esta solicitud no tiene respuesta',
        pt: 'This request has no response',
        en: 'Esta solicitação não tem resposta',
      },
      type: ' ',
      in: ' ',
    });
  }

  const [responseParameters, setResponseParameters] = useState(initResponseParameters);

  const errorParameters = (() => {
    const isSuccessHttpCode = responseKey => responseKey[0] === '2';
    const result = Object.keys(apiData.responses).filter(responseKey => !isSuccessHttpCode(responseKey)).map(responseKey => ({
      name: responseKey,
      description: apiData.responses[responseKey].description,
      properties: apiData.responses[responseKey]?.content?.['application/json']?.schema?.properties?.errorKey?.enum,
    }));

    return result;
  })();

  useEffect(() => {
    setResponseData(getResult() || {});
  }, [newApiData]);

  useEffect(() => {
    const respParameters = Parser.parseRequestBodyProperties(
      responseData.type === 'array'
        ? responseData.items.properties
        : responseData.properties,
    );

    if (respParameters.length <= 0) {
      respParameters.push({
        description:
        {
          es: 'Esta solicitud no tiene respuesta',
          pt: 'This request has no response',
          en: 'Esta solicitação não tem resposta',
        },
        type: ' ',
        in: ' ',
      });
    }

    setResponseParameters(respParameters);
  }, [responseData]);

  useEffect(() => {
    setNewApiData(actualParams.apiData);
  }, [actualParams]);

  useEffect(() => {
    setActualParams({ ...actualParams, codeContentRequestData, requestParameters });
  }, []);

  useEffect(() => {
    window?.Prism?.highlightAll();
  }, [codeContentRequest, responseDataContent]);

  useEffect(() => {
    updateActualPageData(paths);
  }, [paths]);

  useEffect(() => {
    setShowAnchorButton(!isResponseContainerVisible);
  }, [isResponseContainerVisible]);

  const customJsonReplacer = (key, value) => {
    if (typeof value === 'object' && Object.keys(value).length <= 0) {
      return null;
    }

    if (typeof value === 'number' && Number.isNaN(value)) {
      return null;
    }

    if (value === '') {
      return undefined;
    }
    return value;
  };

  const parsePathWithVariables = (originalPath, parameters) => {
    let newPath = originalPath;
    let substitutionsMade = false;

    if (codeType !== 'bash') {
      return originalPath;
    }

    parameters?.forEach(parameter => {
      if (!parameter?.example && !parameter?.schema?.example) {
        return;
      }
      newPath = newPath.replace(`{${parameter.name}}`, parameter?.example || parameter?.schema?.example);
      substitutionsMade = true;
    });

    if (!substitutionsMade) {
      return originalPath;
    }

    return newPath;
  };


  useEffect(() => {
    const newRequestParamsData = getRequestParameters(newApiData);
    const newHeaderData = Parser.getHeaderParamContent(newRequestParamsData, appCredentials?.access_token, apiEndpoint);
    const newBodyData = Parser.getRequestContentForLiveTest(newRequestParamsData);
    const newQueryStringData = Parser.getQueryParamContent(newRequestParamsData);
    let bodyString = JSON.stringify(newBodyData, customJsonReplacer, 2);
    if (bodyString === 'null') {
      bodyString = '{}';
    }
    const pathWithVariables = parsePathWithVariables(apiEndpoint, newRequestParamsData);

    const newCodeContentRequest = Parser.getContentRequest(
      apiMethod,
      serverEndpoint,
      pathWithVariables,
      newQueryStringData,
      bodyString,
      newHeaderData,
      referenceContent,
      codeType,
    );
    setCodeContentRequest(newCodeContentRequest);
    setRequestParamsData(newRequestParamsData);
  }, [actualParams, newApiData, codeType]);


  useEffect(() => {
    const namePageForTracker = `${apiMethod} - ${apiEndpoint}`;
    Tracker.send('mpdevelopers/api-ref/page-view', namePageForTracker);
    const newHeaderData = Parser.getHeaderParamContent(requestParamsData, appCredentials?.access_token, apiEndpoint);
    const newBodyData = Parser.getRequestContentForLiveTest(requestParamsData);
    const newQueryStringData = Parser.getQueryParamContent(requestParamsData);
    let bodyString = JSON.stringify(newBodyData, customJsonReplacer, 2);
    if (bodyString === 'null') {
      bodyString = '{}';
    }
    const pathWithVariables = parsePathWithVariables(apiEndpoint, requestParamsData);

    const newCodeContentRequest = Parser.getContentRequest(
      apiMethod,
      serverEndpoint,
      pathWithVariables,
      newQueryStringData,
      bodyString,
      newHeaderData,
      referenceContent,
      codeType,
    );
    setCodeContentRequest(newCodeContentRequest);
  }, [requestParamsData, codeType]);

  const responseTitle = liveTestResponse ? i18n.gettext('Respuesta') : i18n.gettext('Respuesta de ejemplo');
  return (
    <>
      <div className="reference-container--content">
        <div className="descriptions">
          <APIDetailsContainer
            apiData={apiData}
            apiMethod={apiMethod}
            apiEndpoint={apiEndpoint}
            setApiSnackbar={setApiSnackbar}
            i18n={i18n}
            lang={lang}
            serverEndpoint={serverEndpoint}
          />
          <div className="divider" />
          <ParamsContainer
            i18n={i18n}
            properties={[...requestParameters]}
            lang={lang}
            codeType={codeType}
            type="request"
            siteId={platform.siteId}
            setUpdateCodeContentRequestFromParams={setUpdateCodeContentRequestFromParams}
          />
          {responseParameters && responseParameters.length > 0 && (
            <ParamsContainer
              i18n={i18n}
              properties={responseParameters}
              lang={lang}
              response
              codeType="bash"
              type="response"
              siteId={platform.siteId}
              setUpdateCodeContentRequestFromParams={setUpdateCodeContentRequestFromParams}
            />
          )}
          <span ref={responseContainerRef} />
          {errorParameters && errorParameters.length > 0 && (
            <ParamsContainer
              i18n={i18n}
              properties={errorParameters}
              lang={lang}
              codeType="bash"
              type="error"
              siteId={platform.siteId}
              setUpdateCodeContentRequestFromParams={setUpdateCodeContentRequestFromParams}
            />
          )}
          {deviceType !== 'mobile' && (
            <div className="right-sidebar-reference">
              <div className="divider" />
              <FeedbackThumbs
                lang={lang}
                siteId={platform.siteId}
              />
            </div>
          )}
        </div>

        <div className="data">
          <div className="data--content">
            <div className="snippets">
              <SnippetContainer
                title={i18n.pgettext('api-ref', 'Solicitud')}
                i18n={i18n}
                codeContent={codeContentRequest}
                setApiSnackbar={setApiSnackbar}
                codeLanguage={codeType}
                type="request"
                copyTrakingData={{
                  category: 'MPDEVELOPERS',
                  action: 'CONTENT_COPY_REQUEST_SNIPPET',
                  label: 'Copy the request snippet',
                }}
                deviceType={deviceType}
                lang={lang}
                responseDataContent={responseDataContent}
                apiData={apiData}
                setNewApiData={setNewApiData}
                newApiData={newApiData}
                siteId={platform.siteId}
                setCodeType={setCodeType}
                setUpdateCodeContentRequestFromParams={setUpdateCodeContentRequestFromParams}
                updateCodeContentRequestFromParams={updateCodeContentRequestFromParams}
              />
              {
                updateCodeContentRequestFromParams && (
                  <Message hierarchy="quiet" color="orange">
                    <span>{i18n.gettext('Al cambiar los parámetros, es posible que el código de la solicitud no funcione correctamente.')}</span>
                  </Message>)
              }
              <SnippetContainer
                title={responseTitle}
                i18n={i18n}
                codeContent={liveTestResponse || codeContentResponse}
                setApiSnackbar={setApiSnackbar}
                codeLanguage="json"
                type="response"
                copyTrakingData={{
                  category: 'MPDEVELOPERS',
                  action: 'CONTENT_COPY_RESPONSE_SNIPPET',
                  label: 'Copy the response snippet',
                }}
                deviceType={deviceType}
                lang={lang}
                responseDataContent={responseDataContent}
              />
            </div>
          </div>
        </div>

        {deviceType === 'mobile' && (
          <FeedbackThumbs
            lang={lang}
            siteId={platform.siteId}
          />
        )}

        <div className={`reference-container__anchor-button ${!showAnchorButton && 'reference-container__anchor-button--hide'}`}>
          <Button onClick={scrollToResponseContainer}>
            <ArrowDown20 /> {i18n.gettext('Más informaciones')}
          </Button>
        </div>
      </div>
      {SSRPortal(<Snackbar
        message={apiSnackbar?.snackbarData?.message}
        color={apiSnackbar?.snackbarData?.type}
        show={apiSnackbar?.snackbarData?.show}
        delay={apiSnackbar?.snackbarData?.delay}
        onChange={(newStatus) => {
          setApiSnackbar(prev => ({
            ...prev,
            snackbarData: {
              show: newStatus,
              message: i18n.gettext('Copiado con éxito.'),
              type: 'green',
              delay: 3000,
            },
          }));
        }}
      />, 'body')}
    </>
  );
};

module.exports = APIReference;
